import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import PageLayout from '../common/layout/PageLayout'
import services from '../../services'
import { COMPLETED_STATUSES } from '../../utils/matchStatus'
import InlineProfileCompletionModal from '../auth/InlineProfileCompletionModal'
import useAuth from '../../hooks/useAuth'
import usePlayerHistoryData from './playerHistory/hooks/usePlayerHistoryData'
import useExpandedLeagues from './playerHistory/hooks/useExpandedLeagues'
import usePlayerHistoryAvailability from './playerHistory/hooks/usePlayerHistoryAvailability'
import PlayerHistoryHeader from './playerHistory/components/PlayerHistoryHeader'
import PlayerAvailabilitySection from './playerHistory/components/PlayerAvailabilitySection'
import LeagueHistoryList from './playerHistory/components/LeagueHistoryList'
import PlayerHistoryBackButton from './playerHistory/components/PlayerHistoryBackButton'
import MatchProposalModalWrapper from './playerHistory/components/MatchProposalModalWrapper'
import './PlayerHistory.css'

export default function PlayerHistory() {
  const { playerId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [showProfileModal, setShowProfileModal] = useState(false)

  const pid = Number(playerId)
  const { fromLeague, leagueId: rawLeagueId, divisionId } = location.state || {}
  const leagueId = rawLeagueId ? parseInt(rawLeagueId, 10) : null

  const { loading, error, playerHistory, fetchPlayerHistory } =
    usePlayerHistoryData(playerId)
  const { expandedLeagueIds, toggleLeague } = useExpandedLeagues({
    fromLeague,
    leagueId,
  })

  const {
    availabilityLoading,
    availabilityError,
    processedOpponents,
    handleProposeMatch,
    handleDirectProposeMatch,
    hasUserOverlap,
    getMatchProposalForOpponent,
    getLatestProposalForOpponent,
    getScheduledMatchForOpponent,
    getExistingMatchWithOpponent,
    currentUserPlayerId,
    allMatches,
    leagueName,
    divisionName,
    proposalModalOpen,
    setProposalModalOpen,
    selectedOpponent,
    proposedTime,
    availabilityStart,
    availabilityEnd,
    directProposal,
    handleMatchProposalConfirm,
  } = usePlayerHistoryAvailability({
    fromLeague,
    leagueId,
    divisionId,
    playerHistory,
  })

  const handleBack = useCallback(() => {
    // location.key is 'default' only on initial load / direct URL access; fall
    // back to /my-leagues instead of navigating to an external page.
    if (location.key !== 'default') {
      navigate(-1)
    } else {
      navigate('/my-leagues')
    }
  }, [navigate, location.key])

  useEffect(() => {
    if (playerHistory?.player && user) {
      const hasNoName =
        !playerHistory.player.first_name?.trim() ||
        !playerHistory.player.last_name?.trim()

      // Only prompt the current user to complete their own profile, never
      // when viewing another player's history page.
      if (
        hasNoName &&
        Number(playerHistory.player.user_id) === Number(user.id)
      ) {
        setShowProfileModal(true)
      }
    }
  }, [playerHistory, user])

  if (loading) {
    return (
      <PageLayout title="Loading Player History...">
        <div className="text-center py-5">
          <div className="spinner-border mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading player history...</p>
        </div>
      </PageLayout>
    )
  }

  if (error || !playerHistory) {
    return (
      <PageLayout title="Player History">
        <div className="alert alert-warning">
          <p>{error || 'Player history not found.'}</p>
          <button
            onClick={handleBack}
            className="btn btn-outline-primary"
            aria-label="Navigate back to previous page"
          >
            ← Back
          </button>
        </div>
      </PageLayout>
    )
  }

  const { player, leagues } = playerHistory

  const overallStats = leagues.reduce(
    (stats, leagueData) => {
      const completedMatches = leagueData.matches.filter((m) =>
        COMPLETED_STATUSES.has(m.status),
      )

      completedMatches.forEach((match) => {
        const outcome = services.match.getMatchOutcomeForPlayer(match, pid)
        if (outcome === 'win') {
          stats.wins += 1
          stats.totalMatches += 1
        } else if (outcome === 'loss') {
          stats.losses += 1
          stats.totalMatches += 1
        }
      })

      return stats
    },
    { wins: 0, losses: 0, totalMatches: 0 },
  )

  const winPercentage =
    overallStats.totalMatches > 0
      ? Math.round((overallStats.wins / overallStats.totalMatches) * 100)
      : 0

  const handleProfileComplete = async () => {
    setShowProfileModal(false)
    await fetchPlayerHistory()
  }

  return (
    <PageLayout
      title={
        `${player.first_name || ''} ${player.last_name || ''}'s Match History`.trim() ||
        'Player History'
      }
      noCard={true}
    >
      <div className="player-history container-fluid">
        <PlayerHistoryHeader
          player={player}
          overallStats={overallStats}
          winPercentage={winPercentage}
        />

        <PlayerAvailabilitySection
          fromLeague={fromLeague}
          leagueId={leagueId}
          leagueName={leagueName}
          divisionName={divisionName}
          availabilityLoading={availabilityLoading}
          availabilityError={availabilityError}
          processedOpponents={processedOpponents}
          handleProposeMatch={handleProposeMatch}
          handleDirectProposeMatch={handleDirectProposeMatch}
          hasUserOverlap={hasUserOverlap}
          getMatchProposalForOpponent={getMatchProposalForOpponent}
          getLatestProposalForOpponent={getLatestProposalForOpponent}
          getScheduledMatchForOpponent={getScheduledMatchForOpponent}
          getExistingMatchWithOpponent={getExistingMatchWithOpponent}
          currentUserPlayerId={currentUserPlayerId}
          allMatches={allMatches}
        />

        <LeagueHistoryList
          leagues={leagues}
          expandedLeagueIds={expandedLeagueIds}
          toggleLeague={toggleLeague}
          pid={pid}
          leagueContext={
            fromLeague && leagueId ? { leagueId, divisionId } : null
          }
        />

        <PlayerHistoryBackButton onBack={handleBack} />
      </div>

      {playerHistory?.player && user && (
        <InlineProfileCompletionModal
          show={showProfileModal}
          onComplete={handleProfileComplete}
          onSkip={() => setShowProfileModal(false)}
          primaryPlayer={playerHistory.player}
          user={user}
          allowSkip={true}
          requireSkillLevel={false}
        />
      )}

      <MatchProposalModalWrapper
        fromLeague={fromLeague}
        leagueId={leagueId}
        proposalModalOpen={proposalModalOpen}
        setProposalModalOpen={setProposalModalOpen}
        selectedOpponent={selectedOpponent}
        proposedTime={proposedTime}
        availabilityStart={availabilityStart}
        availabilityEnd={availabilityEnd}
        directProposal={directProposal}
        handleMatchProposalConfirm={handleMatchProposalConfirm}
      />
    </PageLayout>
  )
}
